import { useEffect, useMemo, useState } from "react";
import type { DashboardSalesDrilldownPageData, DashboardSalesPrimaryData, DashboardSnapshot, Section } from "./domain";
import { Icon } from "./icons";
import { russianPlural } from "./russianPlural";
import { DocumentWorkspace } from "./DocumentWorkspace";
import { RegistryPagination } from "./RegistryPagination";
import { RussianDateField } from "./RussianDateField";
import { formatMoscowDate, formatMoscowDateTime } from "./dateFormat";
import { requestNavigationTransition } from "./navigationTransition";
import { dashboardOrderStages, dashboardPeriodError, fiscalPrimaryHref } from "./dashboardNavigation";

interface DashboardProps {
  onNavigate: (section: Section) => void;
  displayName: string;
}

type PeriodPreset = "today" | "week" | "month" | "year" | "custom";

const number = new Intl.NumberFormat("ru-RU");
const money = new Intl.NumberFormat("ru-RU", {
  style: "currency", currency: "RUB", minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const cashMoney = new Intl.NumberFormat("ru-RU", {
  style: "currency", currency: "RUB", minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const businessTimeZone = "Europe/Moscow";
const dateLabel = { format: (value: Date) => formatMoscowDate(value) };
const salesDrilldownLimit = 25;
const salesKindLabel: Record<DashboardSalesPrimaryData["kind"], string> = {
  shipment: "Расходная накладная",
  retail_sale: "Sandbox-продажа",
  customer_return: "Возврат покупателя",
  fiscal_sale: "Реальный кассовый чек",
  fiscal_return: "Фискальный возврат",
};

function businessIsoDate(value: Date) {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: businessTimeZone, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(value);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function shiftIsoDate(value: string, days: number) {
  const [year = 1970, month = 1, day = 1] = value.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, "0")}-${String(shifted.getUTCDate()).padStart(2, "0")}`;
}

function presetPeriod(preset: Exclude<PeriodPreset, "custom">) {
  const today = businessIsoDate(new Date());
  if (preset === "today") return { from: today, to: today };
  const [year = 1970, month = 1, day = 1] = today.split("-").map(Number);
  let from: string;
  if (preset === "week") {
    const mondayOffset = (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
    from = shiftIsoDate(today, -mondayOffset);
  } else if (preset === "month") {
    from = `${year}-${String(month).padStart(2, "0")}-01`;
  } else {
    from = `${year}-01-01`;
  }
  return { from, to: today };
}

function openRegistry(
  section: Section,
  prefix?: string,
  values: Record<string, string> = {},
  focusId?: string,
  view?: string,
) {
  const params = new URLSearchParams();
  if (view) params.set("view", view);
  if (prefix) {
    for (const [key, value] of Object.entries(values)) {
      if (value) params.set(`${prefix}.${key}`, value);
    }
  }
  requestNavigationTransition(() => {
    if (focusId) sessionStorage.setItem("pharmhub:registry-focus", focusId);
    window.location.hash = `#/${section}${params.size ? `?${params}` : ""}`;
  });
}

export function Dashboard({ onNavigate, displayName }: DashboardProps) {
  const firstName = displayName.trim().split(/\s+/)[0] || "коллега";
  const initial = useMemo(() => presetPeriod("today"), []);
  const [preset, setPreset] = useState<PeriodPreset>("today");
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [loadedData, setData] = useState<DashboardSnapshot | null>(null);
  const periodError = dashboardPeriodError(dateFrom, dateTo);
  const data = !periodError && loadedData?.period.dateFrom === dateFrom && loadedData.period.dateTo === dateTo ? loadedData : null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [salesDrilldownOpen, setSalesDrilldownOpen] = useState(false);
  const [salesDrilldown, setSalesDrilldown] = useState<DashboardSalesDrilldownPageData | null>(null);
  const [salesDrilldownLoading, setSalesDrilldownLoading] = useState(false);
  const [salesDrilldownError, setSalesDrilldownError] = useState("");
  const [salesDrilldownOffset, setSalesDrilldownOffset] = useState(0);
  const [salesDrilldownSort, setSalesDrilldownSort] = useState<"occurred_desc" | "occurred_asc">("occurred_desc");

  useEffect(() => {
    if (dashboardPeriodError(dateFrom, dateTo)) { setLoading(false); setData(null); return; }
    const controller = new AbortController();
    const params = new URLSearchParams({ dateFrom, dateTo });
    setLoading(true);
    setError("");
    fetch(`/api/v1/dashboard/snapshot?${params}`, {
      signal: controller.signal, credentials: "include",
    })
      .then(async (response) => {
        if (!response.ok) {
          const detail = (await response.json().catch(() => null))?.detail;
          throw new Error(detail || "Не удалось загрузить показатели");
        }
        return response.json() as Promise<DashboardSnapshot>;
      })
      .then((value) => { if (!controller.signal.aborted) setData(value); })
      .catch((problem: unknown) => {
        if (!controller.signal.aborted) {
          setError(problem instanceof Error ? problem.message : "Не удалось загрузить показатели");
        }
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (!salesDrilldownOpen || dashboardPeriodError(dateFrom, dateTo)) return;
    const controller = new AbortController();
    const params = new URLSearchParams({
      dateFrom, dateTo, sort: salesDrilldownSort,
      offset: String(salesDrilldownOffset), limit: String(salesDrilldownLimit),
    });
    setSalesDrilldownLoading(true);
    setSalesDrilldown(null);
    setSalesDrilldownError("");
    fetch(`/api/v1/dashboard/sales-drilldown?${params}`, {
      signal: controller.signal, credentials: "include",
    })
      .then(async (response) => {
        if (!response.ok) {
          const detail = (await response.json().catch(() => null))?.detail;
          throw new Error(detail || "Не удалось раскрыть первичные документы");
        }
        return response.json() as Promise<DashboardSalesDrilldownPageData>;
      })
      .then((value) => { if (!controller.signal.aborted) setSalesDrilldown(value); })
      .catch((problem: unknown) => {
        if (!controller.signal.aborted) {
          setSalesDrilldownError(problem instanceof Error ? problem.message : "Не удалось раскрыть первичные документы");
        }
      })
      .finally(() => { if (!controller.signal.aborted) setSalesDrilldownLoading(false); });
    return () => controller.abort();
  }, [salesDrilldownOpen, dateFrom, dateTo, salesDrilldownOffset, salesDrilldownSort]);

  function selectPreset(next: PeriodPreset) {
    setPreset(next);
    if (next !== "custom") {
      const period = presetPeriod(next);
      setDateFrom(period.from);
      setDateTo(period.to);
    }
  }

  function openSalesDrilldown() {
    setSalesDrilldownOffset(0);
    setSalesDrilldownSort("occurred_desc");
    setSalesDrilldown(null);
    setSalesDrilldownError("");
    setSalesDrilldownOpen(true);
  }

  function openSalesPrimary(value: DashboardSalesPrimaryData) {
    setSalesDrilldownOpen(false);
    const fiscalHref = fiscalPrimaryHref(value);
    if (fiscalHref) {
      requestNavigationTransition(() => { window.location.hash = fiscalHref; });
      return;
    }
    if (value.registrySection === "retail") {
      openRegistry("retail", "retailRegistry", {
        kind: "sales", q: value.registryQuery, status: "completed_sandbox",
      }, "retail-registers", "registers");
      return;
    }
    openRegistry("sales", "salesDocumentsRegistry", {
      q: value.registryQuery, status: "posted",
    }, "sales-documents-registry", "documents");
  }

  const periodFilters = { from: dateFrom, to: dateTo };
  const empty = Boolean(data &&
    data.orders.created === 0 && data.sales.documents === 0 && data.receipts.documents === 0 &&
    (data.payments?.documents ?? 0) === 0 && data.catalog.products === 0 &&
    data.structure.legalEntities === 0 && data.structure.warehouses === 0);
  const periodEmpty = Boolean(data && !empty && data.orders.created === 0 && data.sales.documents === 0
    && data.sales.returns === 0 && data.receipts.documents === 0 && (data.payments?.documents ?? 0) === 0);

  if (salesDrilldownOpen) {
    return <DocumentWorkspace
      title="Проведённые продажи за период"
      subtitle={`${dateLabel.format(new Date(`${dateFrom}T12:00:00`))} — ${dateLabel.format(new Date(`${dateTo}T12:00:00`))}`}
      status={<span className="document-status document-status--posted">Первичные документы</span>}
      onClose={() => setSalesDrilldownOpen(false)}
    >
      {salesDrilldown && <section className="dashboard-drilldown-summary" aria-label="Сверка показателя продаж">
        <div><span>Продаж</span><strong>{number.format(salesDrilldown?.salesDocuments ?? 0)}</strong></div>
        <div><span>Возвратов</span><strong>{number.format(salesDrilldown?.returns ?? 0)}</strong></div>
        <div><span>Строк первичных документов</span><strong>{number.format(salesDrilldown?.total ?? 0)}</strong></div>
        <div className="dashboard-drilldown-summary__total"><span>Чистая выручка</span><strong>{money.format((salesDrilldown?.revenueMinor ?? 0) / 100)}</strong></div>
      </section>}
      <div className="dashboard-drilldown-toolbar">
        <p>Сумма продаж минус проведённые возвраты. Период и сортировка — по дате документа, как в первичном реестре. Заказы отдельно к выручке не прибавляются.</p>
        <label><span>Сортировка</span><select value={salesDrilldownSort} onChange={(event) => { setSalesDrilldownSort(event.target.value as "occurred_desc" | "occurred_asc"); setSalesDrilldownOffset(0); }}><option value="occurred_desc">Сначала новые</option><option value="occurred_asc">Сначала старые</option></select></label>
      </div>
      {salesDrilldownError && <div className="dashboard-drilldown-error"><Icon name="settings" size={18}/><span><strong>Первичные документы недоступны</strong>{salesDrilldownError}</span></div>}
      {salesDrilldownLoading && <p role="status">Загружаем первичные документы…</p>}
      <div className={`document-lines-wrap dashboard-drilldown-table ${salesDrilldownLoading ? "dashboard-drilldown-table--loading" : ""}`}>
        <table className="document-lines"><thead><tr><th>Документ</th><th>Тип</th><th>Дата документа</th><th className="numeric">Вклад в выручку</th><th/></tr></thead><tbody>{salesDrilldown?.items.map((value) => <tr key={`${value.kind}-${value.id}`}><td data-label="Документ"><strong>{value.number}</strong></td><td data-label="Тип">{salesKindLabel[value.kind]}</td><td data-label="Дата документа">{dateLabel.format(new Date(value.occurredAt))}</td><td data-label="Вклад в выручку" className={`numeric ${value.amountMinor < 0 ? "danger-text" : ""}`}>{money.format(value.amountMinor / 100)}</td><td data-label="Действие"><button className="text-button" onClick={() => openSalesPrimary(value)}>Открыть первичный реестр →</button></td></tr>)}</tbody></table>
        {!salesDrilldownLoading && !salesDrilldownError && (salesDrilldown?.items.length ?? 0) === 0 && <div className="document-lines-empty">Проведённых продаж и возвратов за период нет</div>}
      </div>
      {salesDrilldown && <RegistryPagination offset={salesDrilldown.offset} limit={salesDrilldown.limit} total={salesDrilldown.total} onChange={setSalesDrilldownOffset}/>}
    </DocumentWorkspace>;
  }

  return (
    <div className="page dashboard-page">
      <section className="page-heading dashboard-heading">
        <div><p className="eyebrow">Показатели</p><h1>Доброе утро, {firstName}</h1><p>Только проведённые документы и текущие данные ФармХаб.</p></div>
      </section>

      <section className="panel dashboard-period-toolbar" aria-label="Период показателей">
        <div className="dashboard-period-presets" role="group" aria-label="Быстрый выбор периода">
          {(["today", "week", "month", "year", "custom"] as PeriodPreset[]).map((value) => (
            <button key={value} className={preset === value ? "active" : ""} onClick={() => selectPreset(value)}>
              {{ today: "Сегодня", week: "Неделя", month: "Месяц", year: "Год", custom: "Свой" }[value]}
            </button>
          ))}
        </div>
        {preset === "custom" && <div className="dashboard-period-dates">
          <label><span>Дата от</span><RussianDateField value={dateFrom} max={dateTo} onChange={setDateFrom} ariaLabel="Период показателей от"/></label>
          <label><span>Дата до</span><RussianDateField value={dateTo} min={dateFrom} onChange={setDateTo} ariaLabel="Период показателей до"/></label>
        </div>}
        <span className="dashboard-period-caption">
          {dateFrom && dateTo ? `${dateLabel.format(new Date(`${dateFrom}T12:00:00`))} — ${dateLabel.format(new Date(`${dateTo}T12:00:00`))}` : "Выберите период"}
        </span>
      </section>

      <p className="dashboard-period-note">Заказы, продажи, поступления и платежи — за выбранный период. Кассы и товарные остатки — текущее состояние, независимо от периода. Внешняя история клиентов не создаёт новые движения денег и товаров.</p>
      {periodError ? <section className="panel dashboard-state" role="alert">{periodError}</section>
        : loading && !data ? <section className="panel dashboard-loading">Собираем подтверждённые показатели…</section>
        : error ? <section className="panel dashboard-state dashboard-state--error"><Icon name="settings" size={25}/><div><h2>Показатели временно недоступны</h2><p>{error}. Неполные суммы не показываются.</p></div></section>
        : data && <>
          <section className={`dashboard-kpi-grid ${loading ? "dashboard-kpi-grid--refreshing" : ""}`} aria-busy={loading}>
            <KpiCard icon="bag" title="Заказы покупателей" value={number.format(data.orders.created)} detail="по дате документа за период" onOpen={() => openRegistry("sales", "ordersRegistry", periodFilters, "working-orders", "orders")}>
              <span>Подтверждено <strong>{number.format(data.orders.confirmed)}</strong></span>
              <span>Исполнено <strong>{number.format(data.orders.fulfilled)}</strong></span>
            </KpiCard>
            <KpiCard icon="store" title="Проведённые продажи" value={number.format(data.sales.documents)} detail={data.sales.revenueMinor === undefined ? russianPlural(data.sales.documents,"документ","документа","документов") : money.format(data.sales.revenueMinor / 100)} onOpen={openSalesDrilldown}>
              <span>Возвратов <strong>{number.format(data.sales.returns)}</strong></span>
            </KpiCard>
            <KpiCard icon="basket" title="Поступления" value={number.format(data.receipts.documents)} detail={data.receipts.amountMinor === undefined ? russianPlural(data.receipts.documents,"приходный документ","приходных документа","приходных документов") : money.format(data.receipts.amountMinor / 100)} onOpen={() => openRegistry("inventory", "purchaseReceiptsRegistry", periodFilters, "purchase-receipts-registry", "receipts")} />
            <KpiCard icon="wallet" title="Проведённые платежи" value={number.format(data.payments?.documents ?? 0)} detail={russianPlural(data.payments?.documents ?? 0,"финансовый документ","финансовых документа","финансовых документов")} onOpen={() => openRegistry("finance", "paymentsRegistry", { ...periodFilters, status: "posted" }, "payment-journal", "payments")}>
              <span>Приход <strong>{money.format((data.payments?.incomingMinor ?? 0) / 100)}</strong></span>
              <span>Расход <strong>{money.format((data.payments?.outgoingMinor ?? 0) / 100)}</strong></span>
            </KpiCard>
            <KpiCard icon="store" title="Аптечная касса" value={data.pharmacyCash ? cashMoney.format(data.pharmacyCash.balanceMinor / 100) : "Нет данных"} detail="текущий расчётный остаток; движения текущего отчётного дня" onOpen={() => openRegistry("operator_cash")}>
              <span>Продажи наличными <strong>{cashMoney.format((data.pharmacyCash?.points.reduce((total, point) => total + point.localCashSalesMinor, 0) ?? 0) / 100)}</strong></span>
              <span>Расходы <strong>{cashMoney.format((data.pharmacyCash?.points.reduce((total, point) => total + point.pharmacyExpensesMinor, 0) ?? 0) / 100)}</strong></span>
              <span>Передано управляющему <strong>{cashMoney.format((data.pharmacyCash?.points.reduce((total, point) => total + point.handoverIssuedMinor, 0) ?? 0) / 100)}</strong></span>
              {(data.pharmacyCash?.incompletePoints ?? 0) > 0 && <span className="danger-text">Требуют сверки <strong>{data.pharmacyCash?.incompletePoints}</strong></span>}
            </KpiCard>
            <KpiCard icon="wallet" title="Касса управляющего" value={cashMoney.format((data.companyCash?.balanceMinor ?? 0) / 100)} detail="текущий расчётный остаток" onOpen={() => openRegistry("operator_cash", undefined, {}, undefined, "company")}>
              <span>Из аптек <strong>{cashMoney.format((data.companyCash?.pharmacyHandoversMinor ?? 0) / 100)}</strong></span>
              <span>Доставки <strong>{cashMoney.format((data.companyCash?.deliveryCashMinor ?? 0) / 100)}</strong></span>
              <span>Прочий приход <strong>{cashMoney.format((data.companyCash?.manualIncomingMinor ?? 0) / 100)}</strong></span>
              <span>Расход <strong>{cashMoney.format((data.companyCash?.manualOutgoingMinor ?? 0) / 100)}</strong></span>
            </KpiCard>
            <KpiCard icon="package" title="Каталог и остатки" value={number.format(data.catalog.products)} detail={`${russianPlural(data.catalog.products,"товар","товара","товаров")} в каталоге`} onOpen={() => onNavigate("catalog")}>
              <span>Есть остаток <strong>{number.format(data.catalog.withStock)}</strong></span>
              <span>В резерве <strong>{number.format(data.catalog.reserved)}</strong></span>
            </KpiCard>
            <KpiCard icon="warehouse" title="Структура" value={number.format(data.structure.warehouses)} detail={russianPlural(data.structure.warehouses,"активный склад","активных склада","активных складов")} onOpen={() => openRegistry("settings", undefined, {}, undefined, "company")}>
              <span>Юрлица <strong>{number.format(data.structure.legalEntities)}</strong></span>
              <span>Поставщики <strong>{number.format(data.structure.suppliers)}</strong></span>
              <span>Точки <strong>{number.format(data.structure.retailPoints)}</strong></span>
            </KpiCard>
          </section>

          <section className="panel dashboard-order-stages" aria-label="Этапы заказов за период">
            <h2>Заказы по этапам</h2>
            <p>Текущие этапы заказов с датой документа в выбранном периоде. Нажмите этап, чтобы открыть этот же набор в реестре.</p>
            <div className="dashboard-stage-lifecycle">
              <span>В работе <strong>{number.format(data.orders.lifecycle?.working ?? 0)}</strong></span>
              <span>Закрыты <strong>{number.format(data.orders.lifecycle?.closed ?? 0)}</strong></span>
              <span>Отменены <strong>{number.format(data.orders.lifecycle?.cancelled ?? 0)}</strong></span>
            </div>
            <div className="dashboard-stage-grid">{Object.entries(dashboardOrderStages).map(([stage, label]) => <button key={stage} className="secondary-button" onClick={() => openRegistry("sales", "ordersRegistry", { ...periodFilters, status: stage }, "working-orders", "orders")}><span>{label}</span><strong>{number.format(data.orders.stages?.[stage] ?? 0)}</strong></button>)}</div>
          </section>

          {data.economics && <section className="panel dashboard-economics">
            <div><p className="eyebrow">Только администратору</p><h2>Валовый результат проведённых продаж</h2><small>Отгрузки, завершённые реальные чеки и sandbox-продажи за вычетом проведённых возвратов. Себестоимость — по складским движениям.</small></div>
            <dl><div><dt>Выручка</dt><dd>{money.format(data.economics.revenueMinor / 100)}</dd></div><div><dt>Себестоимость</dt><dd>{money.format(data.economics.costMinor / 100)}</dd></div><div className={data.economics.grossProfitMinor < 0 ? "negative" : "positive"}><dt>Валовая прибыль</dt><dd>{money.format(data.economics.grossProfitMinor / 100)}</dd></div></dl>
          </section>}

          {periodEmpty && <section className="panel dashboard-state dashboard-state--empty"><div><h2>За этот период операций нет</h2><p>Выберите другой период, чтобы посмотреть ранее проведённые документы. Текущие кассы и остатки показаны независимо от этого фильтра.</p><button className="secondary-button" onClick={() => selectPreset("year")}>Показать текущий год</button></div></section>}
          {empty && <section className="panel dashboard-state dashboard-state--empty"><Icon name="chart" size={25}/><div><h2>Рабочих данных пока нет</h2><p>Создайте организацию, склад и товары. Показатели движения появятся только после проведения документов.</p></div><div className="dashboard-empty-actions"><button className="primary-button" onClick={() => onNavigate("settings")}>Настроить структуру</button><button className="secondary-button" onClick={() => onNavigate("catalog")}>Добавить товар</button></div></section>}

          {data.attention.length > 0 && <section className="panel dashboard-attention"><div className="panel__header"><div><h2>Требует внимания</h2><p>Только факты текущей проекции</p></div></div>{data.attention.map((item) => <button key={`${item.section}-${item.title}`} onClick={() => item.section === "retail" ? openSalesDrilldown() : onNavigate(item.section)}><span className={`attention-icon attention-icon--${item.kind}`}><Icon name="alert" size={17}/></span><span><strong>{item.title}</strong><small>{item.detail}</small></span><em>{item.action} →</em></button>)}</section>}

          <p className="dashboard-generated">Обновлено {formatMoscowDateTime(data.generatedAt)}</p>
        </>}
    </div>
  );
}

function KpiCard({ icon, title, value, detail, onOpen, children }: {
  icon: string; title: string; value: string; detail: string; onOpen: () => void; children?: React.ReactNode;
}) {
  return <article className="panel dashboard-kpi-card">
    <div className="dashboard-kpi-card__heading"><i><Icon name={icon} size={19}/></i><span>{title}</span></div>
    <strong>{value}</strong><p>{detail}</p>
    {children && <div className="dashboard-kpi-card__breakdown">{children}</div>}
    <button className="dashboard-kpi-card__open" onClick={onOpen}>Открыть реестр <span aria-hidden="true">→</span></button>
  </article>;
}
